import {
  BdsButton,
  BdsButtonIcon,
  BdsGrid,
  BdsPaper,
  BdsTable,
  BdsTableBody,
  BdsTableCell,
  BdsTableHeader,
  BdsTableRow,
  BdsTableTh,
  BdsTypo,
} from 'blip-ds/dist/blip-ds-react/components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyContainer } from '../../components/EmptyContainer/EmptyContainer';
import { Modal } from '../../components/Modal';
import { Pagination } from '../../components/Pagination';
import { Warning } from '../../components/Warning/Warning';
import { COLUMN_ALIGNMENTS, ITEMS_PER_PAGE_OPTIONS } from '../../constants';
import { useAppContext } from '../../context/AppContext';
import { Category } from '../../interfaces';
import { deleteCategory } from '../../services/CategoryService';
import './CategorySelection.scss';


const CategorySelection = () => {
  const { t } = useTranslation();
  const { setPage, categories, setCategories, setSelectedCategory, botList, apiService } = useAppContext();
  const [rawData, setRawData] = useState(categories);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(ITEMS_PER_PAGE_OPTIONS[0]);
  const paginatedData = rawData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const [categoryToDelete, setCategoryToDelete] = useState<Category>({} as Category);
  const [loading, setLoading] = useState<boolean>(false);

  const heading = [
    t('go-personal-category-selection-table-category'),
    t('go-personal-category-selection-table-quick-replies'),
    t('go-personal-category-selection-table-actions'),
  ];

  useEffect(() => {
    setRawData(categories);
  }, [categories]);

  const confirmDelete = async (item: Category) => {
    setLoading(true);
    const result = await deleteCategory(apiService, item.id ?? '', botList);
    if (result === 'success') {
      setCategories(prevCategories => prevCategories.filter(category => category.id !== item.id));
    }
    setIsWarningOpen(false);
    setLoading(false);
  };

  const handleCreateCategory = (name: string) => {
    const new_category = { id: String(Date.now()), name: name, replies: [] };
    setSelectedCategory(new_category);
    setPage('RepliesList');
  };

  const handleDelete = (item: Category) => {
    setCategoryToDelete(item);
    setIsWarningOpen(true);
  };

  const handleOpen = (item: Category) => {
    setSelectedCategory(item);
    setPage('RepliesList');
  };

  const handleChangeItemsPerPage = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <BdsPaper elevation="none" width="100%" bg-color="surface-0" class="category-paper">
      <BdsGrid padding="none" direction="column" class="category-content">
        <BdsGrid padding="none" direction="column" gap="2">
          <BdsGrid padding="none" gap="2" alignItems="center" justifyContent="space-between" class="category-header">
            <BdsTypo>{t('go-personal-category-selection-description')}</BdsTypo>
            <BdsButton variant="primary" icon="add" onBdsClick={() => setIsModalOpen(true)}>
              {t('go-personal-category-selection-create-button')}
            </BdsButton>
          </BdsGrid>
          {paginatedData.length == 0 ? (
            <EmptyContainer
              title={t('go-personal-category-selection-empty-title')}
              subtitle={t('go-personal-category-selection-empty-subtitle')}
            />
          ) : (
            <BdsTable class="table">
              <BdsTableHeader>
                <BdsTableRow>
                  {heading.map((item, index) => (
                    <BdsTableTh key={`header-${item}-${index}`} justifyContent={COLUMN_ALIGNMENTS[index]}>
                      <BdsTypo variant="fs-14" bold={'semi-bold'}>
                        {item}
                      </BdsTypo>
                    </BdsTableTh>
                  ))}
                </BdsTableRow>
              </BdsTableHeader>
              <BdsTableBody>
                {paginatedData?.map((category, i) => (
                  <BdsTableRow key={category.id ?? `category-${i}`}>
                    <BdsTableCell>
                      <BdsTypo>{category.name}</BdsTypo>
                    </BdsTableCell>
                    <BdsTableCell justifyContent="center">
                      <BdsTypo>{category.replies.length}</BdsTypo>
                    </BdsTableCell>
                    <BdsTableCell type="custom" justifyContent="right">
                      <BdsGrid justifyContent="flex-end" padding="none">
                        <BdsButtonIcon
                          size="short"
                          variant="secondary"
                          icon="trash"
                          onClick={() => handleDelete(category)}
                        ></BdsButtonIcon>
                        <BdsButtonIcon
                          size="short"
                          variant="secondary"
                          icon="edit"
                          onClick={() => handleOpen(category)}
                        />
                      </BdsGrid>
                    </BdsTableCell>
                  </BdsTableRow>
                ))}
              </BdsTableBody>
            </BdsTable>
          )}
          {rawData.length >= ITEMS_PER_PAGE_OPTIONS[0] && (
            <Pagination
              totalItems={rawData.length}
              itemsPerPage={itemsPerPage}
              itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
              onChange={setCurrentPage}
              onItemsPerPageChange={handleChangeItemsPerPage}
            />
          )}
        </BdsGrid>
        <Modal
          buttonAction={e => handleCreateCategory(e)}
          button_text={t('go-personal-category-selection-modal-create-button')}
          input_example={t('go-personal-category-selection-modal-input-example')}
          input_label={t('go-personal-category-selection-modal-input-label')}
          open={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
          title={t('go-personal-category-selection-modal-title')}
          subtitle={t('go-personal-category-selection-modal-subtitle')}
          max_length={60}
          loading={loading}
        />
        <Warning
          closeText={t('go-personal-category-selection-warning-cancel')}
          handleCloseButton={() => setIsWarningOpen(false)}
          handleSubmitButton={() => confirmDelete(categoryToDelete)}
          text={
            <>
              {t('go-personal-category-selection-warning-text-before')} {categoryToDelete.name},{' '}
              <strong>{t('go-personal-category-selection-warning-text-all-replies')}</strong>{' '}
              {t('go-personal-category-selection-warning-text-middle')}{' '}
              <strong>{t('go-personal-category-selection-warning-text-all-numbers')}</strong>{' '}
              {t('go-personal-category-selection-warning-text-after')}
            </>
          }
          title={t('go-personal-category-selection-warning-title')}
          submitText={t('go-personal-category-selection-warning-delete')}
          variant="delete"
          isOpen={isWarningOpen}
          setIsOpen={e => setIsWarningOpen(e)}
          loading={loading}
        />
      </BdsGrid>
    </BdsPaper>
  );
};

export default CategorySelection;
